import { Component, OnInit, Input, ViewChild } from "@angular/core";
import { IonSearchbar, PopoverController } from "@ionic/angular";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { EventsService } from "src/app/services/event/event.service";
import { GameHubControllerService } from "src/app/services/gameHub/game-hub-controller";
import { LobbyService } from "src/app/services/lobby/lobby.service";
import { LobbyStateService } from "src/app/services/lobbyState/lobby-state.service";
import { MobileDataService } from "src/app/services/mobile-data/mobile-data.service";
import { UserSessionService } from "src/app/services/user-session/user-session.service";
import { Navigator } from "../../menu/navigator";
import { LobbyNavigator } from "src/app/services/lobby/lobby-navigator.service";
import { KeywordsService } from "src/app/services/lobby/keywords.service";

@Component({
  selector: "app-mobile-searchbar",
  templateUrl: "./searchbar.component.html",
  styleUrls: ["./searchbar.component.scss"],
})
export class SearchbarComponent implements OnInit {
  @ViewChild(IonSearchbar) myInput: IonSearchbar;
  @Input() keyWordsListData: any;
  @Input() gameListData: any = [];
  @Input() isIOSApp: boolean = true;
  public showKeyWords: boolean = false;
  public hideGames: boolean = true;
  public showCloseButton: boolean = false;
  public keyWordsArray: any = [];
  public keyWordQueryChanged: Subject<any> = new Subject<any>();
  public isAuthenticated: boolean = false;
  public imagesPath: string;
  public filteredGames: any[] = [];

  // "https://sdtest-cdn.palainteractive.com/assets/social/games/"; // this.LobbyService.galleryImagePath;

  constructor(
    public events: EventsService,
    public gameHubController: GameHubControllerService,
    public lobbyService: LobbyService,
    public lobbyState: LobbyStateService,
    public popoverController: PopoverController,
    public userSession: UserSessionService,
    public mobileData: MobileDataService,
    public keywordsService: KeywordsService,
    public navigator: Navigator,
    public lobbyNavigator: LobbyNavigator
  ) {
    this.keyWordQueryChanged
      .pipe(debounceTime(100), distinctUntilChanged())
      .subscribe((model) => {
        this.keyWordsArray = model;
        this.lobbyState.setSearchTerm(this.keyWordsArray);
        this.searchGames();
      });

  }

  ngOnInit() {
    this.userSession.isAuthenticated().then(isAuthenticated => {
      if (isAuthenticated) {
        this.isAuthenticated = true;
      }
    });
    this.imagesPath = this.lobbyService.galleryImagePath;
    this.userSession.getUserStatus().subscribe((userStatus) => {
      if (userStatus == null) this.isAuthenticated = false;
      else this.isAuthenticated = true;
    });

   
  }

  async searchGames() {

    if (!this.keyWordsArray || this.keyWordsArray.trim() === '' || this.keyWordsArray.length < 3) {
      this.filteredGames = [];
      return;
    }

    const searchTerm = this.keyWordsArray.toLowerCase();
    
    const matches = [];
    
    for (const game of this.gameListData) {
      if (!game) continue;
     
      let isMatch = false;
     
      if (game.game_name && game.game_name.toLowerCase().includes(searchTerm)) {
        isMatch = true;
      } 
      else if (game.tags && Array.isArray(game.tags)) {
        for (const tag of game.tags) {
          if (tag && tag.toLowerCase().includes(searchTerm)) {
            isMatch = true;
            break;
          }
        }
      }
      
      if (isMatch && !matches.some(match => match.id === game.id)) {
        matches.push(game);
      }
    }
    
    matches.sort((a, b) => {
      return a.game_name.toLowerCase().localeCompare(b.game_name.toLowerCase());
    });
    
    this.filteredGames = matches;
    
  }

  onDataSelected(data) {
    this.showKeyWords = false;
    this.mobileData.setMobileLobbyKeyword({
      topKeyword: data.name,
      bottomKeyword: null,
    });
    this.events.publish("searchbar:keyword:click", data);
    this.hideGamesList();
  }

  onFieldChange(keywordQuery) {
    this.showKeyWords = false;
    if (keywordQuery == "") {
      this.showKeyWords = true;
      this.resetSearch();
      return;
    } else {
      this.hideGames = false;
    }
    this.keyWordQueryChanged.next(keywordQuery);
  }

  onFocus() {
    if (this.keyWordsArray == "") this.showKeyWords = true;
    this.showCloseButton = true;
    // event:searchbar is communicating to src\modules\lobby\pages\casino\casino.ts
    //KP this.events.publish("event:searchbar:active");
  }

  onBlur(): void {
    setTimeout(() => {
      this.hideGames = true;
      this.showKeyWords = false;
      if (this.keyWordsArray == "") this.showCloseButton = false; 
      this.onCloseClick();
    }, 500);
  }

  onGameClick(gameData) {
    if(gameData.is_demoPlay){
      this.navigateToGameDetails(gameData);
    } else {
      this.launchCasinoGame(gameData);
    }
  }

  navigateToGameDetails(gameData) {
    this.navigator.navigateTo('GameDetailsComponent', { multipleModals: false }, { gamedata: gameData });
  }

  launchCasinoGame(gameData) {
    if (!this.isAuthenticated) {
      if(this.checkIfLiveGame(gameData)){
        setTimeout(() => {
          this.singleMultipleLiveDealerFlow(gameData);
          return this.onLogin();
        }, 500);
      } else {
        this.lobbyService.setWaitingGameData(gameData);
        this.onLogin();
      }
    } else {
      this.hideGamesList();
      this.closeGame();
      if(this.checkIfLiveGame(gameData)) return this.liveGameWorkflow(gameData);
      setTimeout(() => {
        this.gameHubController.launchCasinoGame(gameData);
      }, 300);
    }
  }

  private checkIfLiveGame = (gameData: any): Boolean => gameData.super_type == 'LIVE_DEALER'? true : false;

  private liveGameWorkflow(gameData: any): any{
    return this.lobbyNavigator.liveDealerBonusPopover(gameData);
  }

  onLogin() {
    this.navigator.navigateTo("LoginComponent");
  }

  onCloseClick() {
    this.showCloseButton = false;
    this.resetSearch();
  }

  closeGame() {
    this.events.publish("event:game:close");
    this.gameHubController.killAllInstances();
  }

  // Call this explicitly when onCloseClick to make sure everything is reset
  resetSearch() {
    this.keyWordsArray = "";
    this.filteredGames = [];
    this.hideGames = true;
    this.lobbyState.setSearchTerm("");
  }

  ngOnDestroy() {
  }

  hideGamesList() {
    this.hideGames = true;
  }

  singleMultipleLiveDealerFlow(gameData){
    let subCategoryData;
    if (this.checkIfLiveGame(gameData)){
      this.keywordsService.getLiveDealerKeywordInfo().subscribe(res => {
        if (res && res.subKeywords?.length > 0) {
          this.lobbyService.setLiveDealerKeywordName(res?.name);
          subCategoryData = res?.subKeywords.filter(entry => {
            const actionParam = JSON.parse(entry.action_param);
            return actionParam.vendor === gameData.vendor_name;
          });
          this.lobbyService.setWaitingGameData(subCategoryData);
          this.lobbyNavigator.selectKeyword(res);
          //this.lobbyNavigator.setCurrentTab('livedealer')
        } else {
          this.lobbyService.setLiveDealerKeywordName(res?.name);
          subCategoryData = gameData;
          this.lobbyService.setWaitingGameData(subCategoryData);
          //this.lobbyNavigator.setCurrentTab('livedealer')
        }
      }).unsubscribe();
  }
}
}
